/**
 * A drop zone for one file: drag it in, or click to choose. Once a file is
 * picked it shows its name, and clicking again replaces it. The native input
 * stays in the label (visually hidden), so keyboard and screen readers work.
 */

import { useState, type ReactNode } from "react";
import { FileText, Upload } from "lucide-react";

export function FileDrop({
  id,
  accept,
  label,
  title,
  hint,
  fileName,
  onFile,
  className = "",
}: {
  id: string;
  accept: string;
  /** The input's accessible name, e.g. "CSV file". */
  label: string;
  /** The call to action before a file is picked. */
  title: ReactNode;
  hint?: ReactNode;
  /** The picked file's name, if any. */
  fileName?: string | null | undefined;
  onFile: (file: File | undefined) => void;
  /** Size and spacing for where it sits, e.g. a height. */
  className?: string;
}) {
  const [over, setOver] = useState(false);
  return (
    <label
      htmlFor={id}
      onDragOver={(e) => (e.preventDefault(), setOver(true))}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => (e.preventDefault(), setOver(false), onFile(e.dataTransfer.files[0]))}
      className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-5 text-center focus-within:outline-2 focus-within:outline-link ${
        over ? "border-link bg-drop-bg" : "border-drop-border hover:border-drop-hover"
      } ${className}`}
    >
      <input
        id={id}
        type="file"
        accept={accept}
        className="sr-only"
        aria-label={label}
        onChange={(e) => {
          onFile(e.target.files?.[0]);
          e.target.value = ""; // picking the same file again still fires
        }}
      />
      <span className="flex size-10 items-center justify-center rounded-full bg-subtle">
        {fileName ? <FileText size={18} aria-hidden /> : <Upload size={18} aria-hidden />}
      </span>
      {fileName ? (
        <span className="text-[13px]">
          <span className="font-semibold">{fileName}</span>
          <span className="text-muted"> · </span>
          <span className="text-link">Replace</span>
        </span>
      ) : (
        <span className="text-[13px] font-semibold">{title}</span>
      )}
      {hint && <span className="text-xs text-muted">{hint}</span>}
    </label>
  );
}
